import { useEffect, useRef, useState } from 'react';
import { audioItems } from '@/mocks/audios';
import AudioPlayer from './AudioPlayer';

export default function AudiosSection() {
  const sectionRef = useRef<HTMLElement>(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="audios"
      ref={sectionRef}
      className="w-full py-16 md:py-24 bg-background-50"
      aria-label="Nossos áudios de cura"
    >
      <div className="max-w-6xl mx-auto px-4 md:px-6">
        <div
          className={`text-center mb-12 md:mb-16 transition-all duration-700 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-8'}`}
        >
          <span className="inline-block text-xs md:text-sm font-semibold text-primary-600 uppercase tracking-wider mb-3">
            Nossos Áudios
          </span>
          <h2 className="font-heading text-2xl md:text-4xl font-bold text-foreground-900 mb-4">
            Escolha sua jornada de cura
          </h2>
          <p className="text-sm md:text-base text-foreground-600 max-w-2xl mx-auto leading-relaxed">
            Cada áudio foi cuidadosamente produzido com frequências binaurais específicas
            para atuar em diferentes áreas da sua vida. Ouça com fones de ouvido para melhor experiência.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 md:gap-8">
          {audioItems.map((audio, index) => (
            <article
              key={audio.id}
              className={`group relative bg-background-50 border border-background-200/70 rounded-2xl p-6 md:p-8 transition-all hover:border-primary-300/50 hover:shadow-lg hover:-translate-y-1 flex flex-col ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-10'}`}
              style={{ transitionDelay: `${index * 120}ms`, transitionDuration: '700ms' }}
              aria-label={`Áudio de ${audio.title}`}
            >
              <div className="w-14 h-14 md:w-16 md:h-16 flex items-center justify-center rounded-2xl bg-primary-100 mb-5 group-hover:bg-primary-200 transition-colors">
                <i className={`${audio.icon} text-primary-600 text-2xl md:text-3xl`} />
              </div>
              <h3 className="font-heading text-lg md:text-xl font-bold text-foreground-900 mb-2">
                {audio.title}
              </h3>
              <p className="text-xs md:text-sm text-primary-600 font-medium mb-3">
                {audio.subtitle}
              </p>
              <p className="text-sm text-foreground-600 leading-relaxed mb-5 flex-1">
                {audio.description}
              </p>

              <div className="flex flex-wrap gap-3 mb-5 text-xs text-foreground-500">
                <span className="flex items-center gap-1.5 whitespace-nowrap">
                  <i className="ri-time-line" /> {audio.duration}
                </span>
                <span className="flex items-center gap-1.5 whitespace-nowrap">
                  <i className="ri-file-music-line" /> {audio.format}
                </span>
              </div>

              <ul className="space-y-2 mb-6">
                {audio.benefits.map((b) => (
                  <li key={b} className="flex items-start gap-2 text-sm text-foreground-700">
                    <i className="ri-check-line text-accent-500 mt-0.5 flex-shrink-0" />
                    {b}
                  </li>
                ))}
              </ul>

              <AudioPlayer
                previewUrl={audio.previewUrl}
                previewDuration={audio.previewDuration}
                label="Ouça uma prévia grátis"
              />

              <div className="mt-5">
                <a
                  href="https://pay.hotmart.com/T106583743Q?checkoutMode=2"
                  onClick={(e) => e.preventDefault()}
                  className="hotmart-fb hotmart__button-checkout block w-full text-center py-3 rounded-full bg-primary-500 text-background-50 font-semibold text-sm transition-all hover:bg-primary-600 cursor-pointer whitespace-nowrap"
                >
                  Quero este áudio
                </a>
              </div>
            </article>
          ))}
        </div>
      </div>
    </section>
  );
}